import { useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  TextInput,
  ActivityIndicator,
  Image,
} from 'react-native';
import { supabase } from '../../lib/supabase';
import { Search, X, UserCheck } from '../../lib/icons';

interface AgentLite {
  id: string;
  full_name: string;
  brokerage: string | null;
  photo_url: string | null;
  status: 'active' | 'inactive';
}

interface Props {
  profileId: string;
  currentAgentId: string | null | undefined;
  currentAgentLinkedAt?: string | null;
  isEditing: boolean;
  onChanged?: (agentId: string | null) => void;
}

// Inline picker for the referring real estate agent on the admin
// Profile Detail page. Read-only when not editing.
const ReferringAgentField = ({
  profileId,
  currentAgentId,
  currentAgentLinkedAt,
  isEditing,
  onChanged,
}: Props) => {
  const [agent, setAgent] = useState<AgentLite | null>(null);
  const [loadingAgent, setLoadingAgent] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<AgentLite[]>([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch the currently linked agent (if any) so we can show a friendly name.
  useEffect(() => {
    let cancelled = false;
    setError(null);
    if (!currentAgentId) {
      setAgent(null);
      return () => {};
    }
    (async () => {
      setLoadingAgent(true);
      const { data, error: fetchErr } = await supabase
        .from('real_estate_agents')
        .select('id, full_name, brokerage, photo_url, status')
        .eq('id', currentAgentId)
        .maybeSingle();
      if (cancelled) return;
      if (fetchErr) {
        console.error('Error loading referring agent:', fetchErr);
        setAgent(null);
      } else {
        setAgent((data as AgentLite) || null);
      }
      setLoadingAgent(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [currentAgentId]);

  // Debounced agent search inside the picker.
  useEffect(() => {
    if (!showPicker) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void runSearch();
    }, 200);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchTerm, showPicker]);

  const runSearch = async () => {
    setSearching(true);
    let query = supabase
      .from('real_estate_agents')
      .select('id, full_name, brokerage, photo_url, status')
      .order('full_name', { ascending: true })
      .limit(25);
    const term = searchTerm.trim();
    if (term) {
      const like = `%${term}%`;
      query = query.or(
        `full_name.ilike.${like},brokerage.ilike.${like},referral_code.ilike.${like}`
      );
    }
    const { data, error: searchErr } = await query;
    if (searchErr) {
      console.error('Error searching agents:', searchErr);
      setResults([]);
    } else {
      setResults((data as AgentLite[]) || []);
    }
    setSearching(false);
  };

  const persistAgent = async (newAgentId: string | null) => {
    setBusy(true);
    setError(null);
    try {
      const { error: updateErr } = await supabase
        .from('profiles')
        .update({ referring_agent_id: newAgentId })
        .eq('id', profileId);
      if (updateErr) throw updateErr;

      // Best-effort audit row when assigning a new agent. The unique
      // constraint on (agent_id, homeowner_id) makes this idempotent.
      if (newAgentId) {
        const { error: auditErr } = await supabase
          .from('real_estate_agent_referrals')
          .upsert(
            {
              agent_id: newAgentId,
              homeowner_id: profileId,
              source: 'admin_assigned',
            },
            { onConflict: 'agent_id,homeowner_id', ignoreDuplicates: true }
          );
        if (auditErr) console.warn('Could not write referral audit row:', auditErr);
      }

      onChanged?.(newAgentId);
      setShowPicker(false);
    } catch (err: any) {
      console.error('Error updating referring agent:', err);
      setError(err?.message || 'Failed to update referring agent');
    } finally {
      setBusy(false);
    }
  };

  const summary = useMemo(() => {
    if (loadingAgent) return 'Loading…';
    if (!agent) return 'None';
    return agent.brokerage ? `${agent.full_name} • ${agent.brokerage}` : agent.full_name;
  }, [agent, loadingAgent]);

  return (
    <View style={styles.field}>
      <Text style={styles.fieldLabel}>Referring Real Estate Agent</Text>
      <View style={styles.row}>
        {agent?.photo_url ? (
          <Image source={{ uri: agent.photo_url }} style={styles.avatar} />
        ) : (
          <View style={[styles.avatar, styles.avatarFallback]}>
            <UserCheck size={16} color="#0f766e" />
          </View>
        )}
        <View style={styles.summaryCol}>
          <Text style={styles.summaryText}>{summary}</Text>
          {currentAgentLinkedAt ? (
            <Text style={styles.summaryMeta}>
              Linked {new Date(currentAgentLinkedAt).toLocaleDateString()}
            </Text>
          ) : null}
        </View>
        {isEditing ? (
          <View style={styles.actions}>
            <Pressable style={styles.actionButton} onPress={() => setShowPicker(true)}>
              <Text style={styles.actionButtonText}>{agent ? 'Change' : 'Assign'}</Text>
            </Pressable>
            {agent && !busy ? (
              <Pressable
                style={[styles.actionButton, styles.actionButtonSubtle]}
                onPress={() => persistAgent(null)}
              >
                <Text style={styles.actionButtonSubtleText}>Clear</Text>
              </Pressable>
            ) : null}
            {busy ? <ActivityIndicator size="small" color="#6b7280" /> : null}
          </View>
        ) : null}
      </View>

      {error ? <Text style={styles.errorText}>{error}</Text> : null}

      {showPicker ? (
        <View style={styles.pickerCard}>
          <View style={styles.pickerHeader}>
            <Text style={styles.pickerTitle}>Pick a referring agent</Text>
            <Pressable onPress={() => setShowPicker(false)} hitSlop={8}>
              <X size={16} color="#6b7280" />
            </Pressable>
          </View>
          <View style={styles.searchRow}>
            <Search size={16} color="#6b7280" />
            <TextInput
              style={styles.searchInput}
              value={searchTerm}
              onChangeText={setSearchTerm}
              placeholder="Search by name, brokerage, or code"
              placeholderTextColor="#9ca3af"
              autoFocus
            />
          </View>
          {searching ? (
            <View style={styles.pickerLoading}>
              <ActivityIndicator size="small" color="#9ca3af" />
            </View>
          ) : results.length === 0 ? (
            <Text style={styles.pickerEmpty}>No agents found.</Text>
          ) : (
            <View style={styles.pickerList}>
              {results.map((r) => {
                const isCurrent = r.id === currentAgentId;
                return (
                  <Pressable
                    key={r.id}
                    style={[styles.pickerItem, isCurrent && styles.pickerItemActive]}
                    onPress={() => persistAgent(r.id)}
                    disabled={busy}
                  >
                    {r.photo_url ? (
                      <Image source={{ uri: r.photo_url }} style={styles.pickerAvatar} />
                    ) : (
                      <View style={[styles.pickerAvatar, styles.avatarFallback]}>
                        <Text style={styles.pickerAvatarText}>
                          {r.full_name.charAt(0).toUpperCase()}
                        </Text>
                      </View>
                    )}
                    <View style={styles.pickerItemInfo}>
                      <Text style={styles.pickerItemName}>{r.full_name}</Text>
                      {r.brokerage ? (
                        <Text style={styles.pickerItemBrokerage}>{r.brokerage}</Text>
                      ) : null}
                    </View>
                    {r.status === 'inactive' ? (
                      <View style={styles.inactiveBadge}>
                        <Text style={styles.inactiveBadgeText}>Inactive</Text>
                      </View>
                    ) : null}
                  </Pressable>
                );
              })}
            </View>
          )}
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  field: {
    gap: 6,
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#e5e7eb',
  },
  avatarFallback: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  summaryCol: {
    flex: 1,
    gap: 2,
  },
  summaryText: {
    fontSize: 14,
    color: '#111827',
    fontWeight: '500',
  },
  summaryMeta: {
    fontSize: 12,
    color: '#6b7280',
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  actionButton: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#d1d5db',
    backgroundColor: '#ffffff',
  },
  actionButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#111827',
  },
  actionButtonSubtle: {
    borderColor: '#fecaca',
  },
  actionButtonSubtleText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#b91c1c',
  },
  errorText: {
    fontSize: 12,
    color: '#dc2626',
  },
  pickerCard: {
    marginTop: 8,
    padding: 12,
    backgroundColor: '#f9fafb',
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 10,
    gap: 10,
  },
  pickerHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  pickerTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: '#111827',
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 8,
    paddingHorizontal: 10,
  },
  searchInput: {
    flex: 1,
    fontSize: 13,
    paddingVertical: 8,
    color: '#111827',
  },
  pickerLoading: {
    paddingVertical: 16,
    alignItems: 'center',
  },
  pickerEmpty: {
    fontSize: 12,
    color: '#9ca3af',
    fontStyle: 'italic',
    paddingVertical: 8,
  },
  pickerList: {
    gap: 4,
    maxHeight: 240,
  },
  pickerItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    padding: 8,
    borderRadius: 8,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: 'transparent',
  },
  pickerItemActive: {
    borderColor: '#99f6e4',
    backgroundColor: '#f0fdfa',
  },
  pickerAvatar: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#e5e7eb',
  },
  pickerAvatarText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#374151',
  },
  pickerItemInfo: {
    flex: 1,
    gap: 2,
  },
  pickerItemName: {
    fontSize: 13,
    fontWeight: '500',
    color: '#111827',
  },
  pickerItemBrokerage: {
    fontSize: 12,
    color: '#6b7280',
  },
  inactiveBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 999,
    backgroundColor: '#f3f4f6',
  },
  inactiveBadgeText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#6b7280',
  },
});

export default ReferringAgentField;
